import { QueryOperator, MatchQueryOperators } from "types";
import { useMemo, useRef, useState } from "react";
import QueryModal from "views/components/QueryModal";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import SubmitQueryButton from "views/components/QueryModal/SubmitQueryButton";
import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import styles from "./style.module.scss";
import useVqlParams from "hooks/useVqlParams";
import { useDispatch } from "react-redux";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import {
  createAndUpdateMediaStatusFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { VqlQuery } from "types";
import { useMultiSelectQueryModal } from "hooks/UseQueryModal/useMultiSelectQueryModal";

interface MediaStatusQueryModalProps {
  editQueryId?: string;
}

// Define the static indexed status options
const INDEXED_STATUS_OPTIONS = [
  { id: "1", text: "Pending Indexed", value: "pending_index", selected: false },
  { id: "2", text: "Indexed", value: "indexed", selected: false },
];

export default function MediaStatusQueryModal({
  editQueryId,
}: MediaStatusQueryModalProps) {
  const { vqlQueries, addVqlQuery, findVqlQuery, updateVqlQuery } =
    useVqlParams();

  const oldQuery = editQueryId
    ? findVqlQuery(editQueryId, "media_status")
    : null;
  // Handle both single value and array for backward compatibility
  let rawQueryValue = oldQuery?.media_status?.value;
  let queryValues = Array.isArray(rawQueryValue)
    ? rawQueryValue
    : rawQueryValue
      ? [rawQueryValue]
      : [];
  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery?.media_status?.op)
    : QueryOperator.IS;
  const [selectedOperator, setSelectedOperator] = useState(queryOperator);

  const existingQuery = useMemo(() => {
    if (editQueryId) {
      return null;
    }
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "media_status") &&
          convertAPIOperator(query.media_status?.op) === selectedOperator,
      ) || null
    );
  }, [vqlQueries, selectedOperator, editQueryId]);

  if (existingQuery && isVqlQuery(existingQuery, "media_status")) {
    let rawExistingValue = existingQuery.media_status?.value;
    queryValues = Array.isArray(rawExistingValue)
      ? rawExistingValue
      : rawExistingValue
        ? [rawExistingValue]
        : [];
  }

  // Initialize options with selection state based on existing query values
  const [options, setOptions] = useState(() =>
    INDEXED_STATUS_OPTIONS.map((option) => ({
      ...option,
      selected: queryValues.includes(option.value),
    })),
  );

  const [searchText, setSearchText] = useState("");
  const dispatch = useDispatch();

  const selectedOptionIds = options
    .filter((option) => option.selected)
    .map((option) => option.id);

  const previousSelectedIds = INDEXED_STATUS_OPTIONS.filter((option) =>
    queryValues.includes(option.value),
  ).map((option) => option.id);

  const modalRef = useRef<HTMLDivElement>(null);

  const { isSubmitDisabled } = useMultiSelectQueryModal({
    selectedIds: selectedOptionIds,
    previousIds: previousSelectedIds,
    selectedOperator,
    initialOperator: queryOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const handleRemoveOption = (id: string) => {
    const newOptions = options.map((option) => {
      if (option.id === id) {
        return { ...option, selected: false };
      }
      return option;
    });
    setOptions(newOptions);
  };

  const handleSelectOption = (id: string) => {
    const newOptions = options.map((option) => {
      // For single selection, deselect all others and select only the clicked one
      return { ...option, selected: option.id === id };
    });
    setOptions(newOptions);
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    setSelectedOperator(operator);
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  const handleSubmission = () => {
    if (isSubmitDisabled) {
      return;
    }

    const selectedOption = options.find((option) =>
      selectedOptionIds.includes(option.id),
    );

    if (!selectedOption) {
      return; // No selection made
    }

    const vqlQuery = createAndUpdateMediaStatusFilter(
      selectedOption.value, // Pass single string value, not array
      selectedOperator as MatchQueryOperators,
      editQueryId || existingQuery?.id,
    );

    editQueryId || existingQuery
      ? updateVqlQuery(vqlQuery)
      : addVqlQuery(vqlQuery);
    dispatch(setSelectedQueryModal(null));
  };

  return (
    <QueryModal modalRef={modalRef}>
      <QueryModalTitle icon={undefined}>Media Status</QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[QueryOperator.IS]}
          selectedOperator={selectedOperator}
          onSelect={handleOptionSelection}
        />
        <QueryModalInput
          mode="single"
          options={options}
          searchText={searchText}
          selected={selectedOptionIds}
          onRemove={handleRemoveOption}
          onSelect={handleSelectOption}
          onInputChange={handleInputChange}
        />
        <SubmitQueryButton
          isDisabled={isSubmitDisabled}
          onSubmit={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
